import React, { useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Filter, Users, Loader2, Plus, AlertTriangle, Search } from "lucide-react";

// Filter criteria interface
export interface CohortFilterCriteria {
  minProducts: string;
  maxProducts: string;
  minRatings: string;
  maxRatings: string;
  minBrands: string;
  maxBrands: string;
  country: string;
  excludeFromTargetLedger: boolean;
  // New filters
  queryable_hasFBA: string; // 'all' | 'true' | 'false'
  minRating: string; // Min rating percentage
  maxRating: string; // Max rating percentage
  excludeSellersFromChina: boolean; // Exclude sellers who ship from China
  minAvgBuyBoxCompetitors: string; // Min average buy box competitors
  maxAvgBuyBoxCompetitors: string; // Max average buy box competitors
  minBuyBoxNewOwnershipRate: string; // Min new buy box ownership rate
  maxBuyBoxNewOwnershipRate: string; // Max new buy box ownership rate
  minBuyBoxUsedOwnershipRate: string; // Min used buy box ownership rate
  maxBuyBoxUsedOwnershipRate: string; // Max used buy box ownership rate
}

interface CohortFilterFormProps {
  filters: CohortFilterCriteria;
  onFilterChange: (key: keyof CohortFilterCriteria, value: string | boolean) => void;
  totalCount: number;
  countLoading: boolean;
  onCreateCohort?: () => void;
  onSearchTrigger?: () => void;
  isValidationInProgress?: boolean;
  formDisabled?: boolean;
}

const CohortFilterForm: React.FC<CohortFilterFormProps> = ({
  filters,
  onFilterChange,
  totalCount,
  countLoading,
  onCreateCohort,
  onSearchTrigger,
  isValidationInProgress = false,
  formDisabled = false
}) => {
  // Handle Enter key press for search trigger
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onSearchTrigger && !formDisabled) {
      onSearchTrigger();
    }
  };

  // Handle input change - NO auto-search, only update state
  const handleTextInputChange = useCallback((key: keyof CohortFilterCriteria, value: string) => {
    // Update the filter state only - NO auto-search
    onFilterChange(key, value);
  }, [onFilterChange]);
  // Country options
  const countryOptions = ["US", "CN", "CA", "UK", "AU", "DE", "FR", "IT", "ES"];

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Filter className="h-5 w-5 mr-2" />
          Sellers Filter
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Numeric Ranges - Single Combined Table */}
        <div className="space-y-2">
          {/* Header Row */}
          <div className="flex items-center gap-2">
            <div className="w-8"></div>
            <div className="grid grid-cols-7 gap-1 flex-1 text-xs font-medium text-gray-600 dark:text-gray-400 pb-2 border-b border-gray-200 dark:border-gray-700">
              <div className="text-center px-1 py-1 border-r border-gray-100 dark:border-gray-700 last:border-r-0">Product Count</div>
              <div className="text-center px-1 py-1 border-r border-gray-100 dark:border-gray-700 last:border-r-0">Rating Count</div>
              <div className="text-center px-1 py-1 border-r border-gray-100 dark:border-gray-700 last:border-r-0">Brand Count</div>
              <div className="text-center px-1 py-1 border-r border-gray-100 dark:border-gray-700 last:border-r-0">Rating (%)</div>
              <div className="text-center px-1 py-1 border-r border-gray-100 dark:border-gray-700 last:border-r-0">New Buy Box Rate (%)</div>
              <div className="text-center px-1 py-1 border-r border-gray-100 dark:border-gray-700 last:border-r-0">Used Buy Box Rate (%)</div>
              <div className="text-center px-1 py-1">Avg. Competitors</div>
            </div>
          </div>
          
          {/* Min Row */}
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 w-8">Min:</div>
            <div className="grid grid-cols-7 gap-1 flex-1">
              <Input
                type="number"
                min="0"
                placeholder="e.g., 10"
                value={filters.minProducts}
                onChange={(e) => handleTextInputChange("minProducts", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                placeholder="e.g., 100"
                value={filters.minRatings}
                onChange={(e) => handleTextInputChange("minRatings", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                placeholder="e.g., 1"
                value={filters.minBrands}
                onChange={(e) => handleTextInputChange("minBrands", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="95.0"
                value={filters.minRating}
                onChange={(e) => handleTextInputChange("minRating", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 10"
                value={filters.minBuyBoxNewOwnershipRate}
                onChange={(e) => handleTextInputChange("minBuyBoxNewOwnershipRate", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 0"
                value={filters.minBuyBoxUsedOwnershipRate}
                onChange={(e) => handleTextInputChange("minBuyBoxUsedOwnershipRate", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                step="0.1"
                placeholder="e.g., 1.0"
                value={filters.minAvgBuyBoxCompetitors}
                onChange={(e) => handleTextInputChange("minAvgBuyBoxCompetitors", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
            </div>
          </div>
          
          {/* Max Row */}
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 w-8">Max:</div>
            <div className="grid grid-cols-7 gap-1 flex-1">
              <Input
                type="number"
                min="0"
                placeholder="e.g., 1000"
                value={filters.maxProducts}
                onChange={(e) => handleTextInputChange("maxProducts", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                placeholder="e.g., 10000"
                value={filters.maxRatings}
                onChange={(e) => handleTextInputChange("maxRatings", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                placeholder="e.g., 9"
                value={filters.maxBrands}
                onChange={(e) => handleTextInputChange("maxBrands", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="100"
                value={filters.maxRating}
                onChange={(e) => handleTextInputChange("maxRating", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 90"
                value={filters.maxBuyBoxNewOwnershipRate}
                onChange={(e) => handleTextInputChange("maxBuyBoxNewOwnershipRate", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="e.g., 50"
                value={filters.maxBuyBoxUsedOwnershipRate}
                onChange={(e) => handleTextInputChange("maxBuyBoxUsedOwnershipRate", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
              <Input
                type="number"
                min="0"
                step="0.1"
                placeholder="e.g., 10"
                value={filters.maxAvgBuyBoxCompetitors}
                onChange={(e) => handleTextInputChange("maxAvgBuyBoxCompetitors", e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={formDisabled}
                className="text-xs h-8"
              />
            </div>
          </div>
        </div>

        {/* Additional Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Fulfillment Status:</Label>
            <Select
              value={filters.queryable_hasFBA || "all"}
              onValueChange={(value) => {
                const newValue = value === "all" ? "" : value;
                onFilterChange("queryable_hasFBA", newValue);
              }}
              disabled={formDisabled}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="All Sellers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sellers</SelectItem>
                <SelectItem value="true">FBA Sellers Only</SelectItem>
                <SelectItem value="false">Non-FBA Sellers Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Country:</Label>
            <Select
              value={filters.country || "US"}
              onValueChange={(value) => {
                const newCountryValue = value === "all" ? "" : value;
                onFilterChange("country", newCountryValue);
              }}
              disabled={formDisabled}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="United States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="US">United States</SelectItem>
                {countryOptions.filter(c => c !== "US").map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="excludeSellersFromChina"
              checked={filters.excludeSellersFromChina}
              onCheckedChange={(checked) => {
                onFilterChange("excludeSellersFromChina", checked);
              }}
              disabled={formDisabled}
            />
            <Label htmlFor="excludeSellersFromChina" className="text-sm">
              Exclude who ship from China
            </Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="excludeTargetLedger"
              checked={filters.excludeFromTargetLedger}
              onCheckedChange={(checked) => {
                onFilterChange("excludeFromTargetLedger", checked);
              }}
              disabled={formDisabled}
            />
            <Label htmlFor="excludeTargetLedger" className="text-sm">
              Exclude sellers in Target Ledger
            </Label>
          </div>
        </div>

        {/* Search Button - Always Visible */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={onSearchTrigger}
            disabled={formDisabled || countLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-6 text-lg"
          >
            {countLoading ? (
              <>
                <Loader2 className="h-6 w-6 mr-3 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-6 w-6 mr-3" />
                Get Seller Count
              </>
            )}
          </Button>
        </div>

        {/* Results Display - Only show after search */}
        {(totalCount > 0 || countLoading) && (
          <>
            {totalCount > 5000 ? (
              /* "TOO LARGE" STATE */
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-red-600 dark:text-red-400" />
                  <span className="text-sm font-medium text-red-900 dark:text-red-100">
                    Total Count: {totalCount.toLocaleString()} sellers
                  </span>
                </div>
                <div className="text-center">
                  <Button
                    disabled={true}
                    size="sm"
                    className="bg-gray-400 hover:bg-gray-400 cursor-not-allowed text-white disabled:opacity-100"
                  >
                    Cohort Too Large
                  </Button>
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Maximum 5,000 sellers allowed.
                  </div>
                </div>
              </div>
            ) : (
              /* NORMAL STATE */
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total Count: {countLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin inline ml-1" />
                    ) : (
                      <span className="font-bold">{totalCount.toLocaleString()} seller{totalCount !== 1 ? 's' : ''}</span>
                    )}
                  </span>
                </div>
                
                <Button
                  disabled={!totalCount || totalCount === 0 || countLoading || isValidationInProgress || formDisabled}
                  size="sm"
                  className="bg-yellow-500 hover:bg-yellow-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={onCreateCohort}
                >
                  {isValidationInProgress ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Cohort ({totalCount.toLocaleString()})
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CohortFilterForm;